/**
 * Event Schema Validation
 *
 * Validates events against the canonical schema before queueing.
 * Invalid events are rejected to prevent data quality issues.
 */

import type {
  CanonicalEvent,
  EventInput,
  EventName,
  ValidationResult,
  ConsentScope,
  ActorType,
} from './types';

// ============================================
// VALID VALUES
// ============================================

const VALID_EVENT_NAMES: Set<EventName> = new Set([
  // Critical - Onboarding
  'onboarding_started',
  'onboarding_step_completed',
  'onboarding_completed',
  // Critical - Mood & Wellness
  'mood_checkin_completed',
  'stress_logged',
  // Critical - AWU
  'awu_session_started',
  'awu_session_completed',
  // Critical - Techniques
  'technique_session_started',
  'technique_session_completed',
  // Critical - Modules
  'module_started',
  'module_completed',
  'module_quiz_completed',
  // Critical - AI Coach
  'ai_coach_conversation_started',
  'ai_coach_message_sent',
  'ai_coach_message_received',
  // Critical - Crisis & Safety
  'crisis_detected',
  'crisis_resource_presented',
  'crisis_resource_accessed',
  'crisis_mode_exited',
  'escalation_triggered',
  'escalation_acknowledged',
  // Secondary - Engagement
  'page_viewed',
  'feature_accessed',
  // Secondary - Journal
  'journal_entry_created',
  'reflection_written',
  // Secondary - Goals
  'goal_created',
  'goal_milestone_completed',
  'goal_completed',
  // Secondary - Brain Gym
  'ef_game_started',
  'ef_game_completed',
  // Secondary - Rewards
  'achievement_unlocked',
  'shop_item_purchased',
  // Secondary - Consent
  'consent_granted',
  'consent_revoked',
  'consent_scope_changed',
  // Secondary - Assessment
  'assessment_started',
  'assessment_completed',
]);

const VALID_CONSENT_SCOPES: Set<ConsentScope> = new Set([
  'personal_only',
  'share_with_parent',
  'share_with_counselor',
  'institution_aggregate_only',
]);

const VALID_ACTOR_TYPES: Set<ActorType> = new Set([
  'user',
  'system',
  'ai_coach',
  'scheduler',
]);

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Validate UUID v4 format
 */
function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Validate ISO 8601 timestamp
 */
function isValidISO8601(str: string): boolean {
  const date = new Date(str);
  return !isNaN(date.getTime()) && str.includes('T');
}

/**
 * Validate event input before creating canonical event
 */
export function validateEventInput(input: EventInput): ValidationResult {
  const errors: string[] = [];

  // Event name is required and must be valid
  if (!input.event_name) {
    errors.push('event_name is required');
  } else if (!VALID_EVENT_NAMES.has(input.event_name)) {
    errors.push(`Invalid event_name: ${input.event_name}`);
  }

  // Payload is required
  if (!input.payload || typeof input.payload !== 'object') {
    errors.push('payload is required and must be an object');
  }

  // Consent scope validation (if provided)
  if (input.consent_scope && !VALID_CONSENT_SCOPES.has(input.consent_scope)) {
    errors.push(`Invalid consent_scope: ${input.consent_scope}`);
  }

  // Actor type validation (if provided)
  if (input.actor_type && !VALID_ACTOR_TYPES.has(input.actor_type)) {
    errors.push(`Invalid actor_type: ${input.actor_type}`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validate canonical event structure
 */
export function validateCanonicalEvent(event: CanonicalEvent): ValidationResult {
  const errors: string[] = [];

  // Required fields
  if (!event.idempotency_key) {
    errors.push('idempotency_key is required');
  } else if (!isValidUUID(event.idempotency_key)) {
    errors.push('idempotency_key must be a valid UUID v4');
  }

  if (!event.event_name) {
    errors.push('event_name is required');
  } else if (!VALID_EVENT_NAMES.has(event.event_name)) {
    errors.push(`Invalid event_name: ${event.event_name}`);
  }

  if (!event.tenant_id) {
    errors.push('tenant_id is required');
  }

  if (!event.actor_type) {
    errors.push('actor_type is required');
  } else if (!VALID_ACTOR_TYPES.has(event.actor_type)) {
    errors.push(`Invalid actor_type: ${event.actor_type}`);
  }

  if (!event.consent_scope) {
    errors.push('consent_scope is required');
  } else if (!VALID_CONSENT_SCOPES.has(event.consent_scope)) {
    errors.push(`Invalid consent_scope: ${event.consent_scope}`);
  }

  if (!event.occurred_at) {
    errors.push('occurred_at is required');
  } else if (!isValidISO8601(event.occurred_at)) {
    errors.push('occurred_at must be a valid ISO 8601 timestamp');
  }

  if (!event.queued_at) {
    errors.push('queued_at is required');
  } else if (!isValidISO8601(event.queued_at)) {
    errors.push('queued_at must be a valid ISO 8601 timestamp');
  }

  if (!event.payload || typeof event.payload !== 'object') {
    errors.push('payload is required and must be an object');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Event-specific payload validation
 * Returns additional errors for malformed payloads
 */
export function validateEventPayload(
  eventName: EventName,
  payload: Record<string, unknown>
): ValidationResult {
  const errors: string[] = [];

  switch (eventName) {
    case 'mood_checkin_completed':
      if (!payload.mood) {
        errors.push('mood_checkin_completed requires mood in payload');
      } else if (!['stressed', 'okay', 'good'].includes(payload.mood as string)) {
        errors.push('mood must be one of: stressed, okay, good');
      }
      break;

    case 'technique_session_completed':
      if (!payload.technique_id) {
        errors.push('technique_session_completed requires technique_id');
      }
      if (typeof payload.duration_ms !== 'number' || payload.duration_ms < 0) {
        errors.push('technique_session_completed requires valid duration_ms');
      }
      break;

    case 'module_completed':
    case 'module_started':
      if (!payload.module_id) {
        errors.push(`${eventName} requires module_id`);
      }
      break;

    case 'module_quiz_completed':
      if (!payload.module_id) {
        errors.push('module_quiz_completed requires module_id');
      }
      if (typeof payload.score !== 'number') {
        errors.push('module_quiz_completed requires score');
      }
      break;

    case 'crisis_detected':
      if (!payload.detection_method) {
        errors.push('crisis_detected requires detection_method');
      }
      if (!payload.severity) {
        errors.push('crisis_detected requires severity');
      }
      if (!payload.trigger_source) {
        errors.push('crisis_detected requires trigger_source');
      }
      break;

    case 'ef_game_completed':
      if (!payload.game_id) {
        errors.push('ef_game_completed requires game_id');
      }
      if (typeof payload.score !== 'number') {
        errors.push('ef_game_completed requires score');
      }
      break;

    case 'consent_scope_changed':
    case 'consent_granted':
    case 'consent_revoked':
      if (!payload.new_scope) {
        errors.push(`${eventName} requires new_scope`);
      }
      break;
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Full validation pipeline for an event
 */
export function validateEvent(event: CanonicalEvent): ValidationResult {
  const canonicalResult = validateCanonicalEvent(event);
  if (!canonicalResult.valid) {
    return canonicalResult;
  }

  const payloadResult = validateEventPayload(
    event.event_name,
    event.payload as Record<string, unknown>
  );

  return {
    valid: payloadResult.valid,
    errors: [...canonicalResult.errors, ...payloadResult.errors],
  };
}
